'use client';
import type { creditType } from '@repo/customer-api';
import { redirect } from 'next/navigation';
import { createContext, use, useEffect } from 'react';

// Only what the lists read: the raw response also carries status flags and messages
export type CreditsResult = { notAuth?: boolean; Mora?: boolean; Credits?: creditType[] };

const CreditsContext = createContext<Promise<CreditsResult> | null>(null);

// Last credits the lists loaded, by IdSolicitud: an operation opened from them shows at once while its own request runs.
// Lives for the browser tab, never on the server; the login page clears it
const lastCredits = new Map<number, creditType>();

/**
 * The operation as the Productos lists last loaded it, if they did.
 *
 * @param {number} id - The operation's IdSolicitud.
 * @return {creditType | undefined} The cached credit.
 */
export const cachedCredit = (id: number): creditType | undefined => lastCredits.get(id);

/** Forgets the cached credits, e.g. when the session ends. */
export const clearCachedCredits = () => lastCredits.clear();

/**
 * Shares one credits request between the Productos tabs. The tabs layout starts it without awaiting and stays mounted
 * while switching tabs, so Operaciones and Pagos read the same data instead of fetching it again on every switch.
 *
 * @param {Promise<CreditsResult>} credits - The pending credits request; must never reject.
 */
export const CreditsProvider = ({
  credits,
  children,
}: {
  credits: Promise<CreditsResult>;
  children: React.ReactNode;
}): React.JSX.Element => {
  useEffect(() => {
    credits.then(({ Credits }) => {
      lastCredits.clear();
      Credits?.forEach((credit) => lastCredits.set(credit.IdSolicitud, credit));
    });
  }, [credits]);

  return <CreditsContext.Provider value={credits}>{children}</CreditsContext.Provider>;
};

/**
 * Suspends until the shared credits arrive. An expired session goes to the login.
 *
 * @return {CreditsResult} Mora and the credits; no Credits when the request failed.
 */
export const useCredits = (): CreditsResult => {
  const credits = use(CreditsContext);
  if (!credits) throw new Error('useCredits must be used within a CreditsProvider');
  const result = use(credits);
  if (result.notAuth) redirect('/login?expired=1');
  return result;
};
